import type { Event } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { isRouteErrorResponse } from "react-router";
import { reportApiError } from "@/services/apiErrorReporting";
import { getSafeErrorDiagnostics } from "@/utils/safeConsole";

export interface RouteErrorReportingDependencies {
  captureException: typeof Sentry.captureException;
  reportApiError: (cause: unknown) => void;
}

const defaultRouteErrorReportingDependencies: RouteErrorReportingDependencies =
  {
    captureException: Sentry.captureException,
    reportApiError,
  };

/**
 * Reports errors surfaced by React Router error boundaries to Sentry.
 * Skips capture when DSN is not configured. `extra` lets callers attach
 * structured context (e.g. chunk-recovery telemetry props).
 */
export function captureClientRouteError(
  cause: unknown,
  extra?: Event["extra"],
  dependencies: RouteErrorReportingDependencies = defaultRouteErrorReportingDependencies,
): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  if (isRouteErrorResponse(cause)) {
    dependencies.captureException(
      new Error(`HTTP ${cause.status}: ${cause.statusText || "route error"}`),
      {
        // extra spreads FIRST: the HTTP fields derive from the error itself
        // and stay authoritative even if a caller's extra carries the same
        // key names (status/statusText/data are easy accidental collisions).
        extra: {
          ...extra,
          status: cause.status,
          statusText: cause.statusText,
          data: cause.data,
        },
      },
    );
    return;
  }

  // A route loader's axios failure reaches this boundary as well as the
  // reporting interceptor, so capturing it here sent the same failure to Sentry
  // twice — once policy-shaped, once as a raw `AxiosError: Network Error` that
  // bypassed every rule in `apiErrorReporting`: no 4xx exclusion, no abort or
  // teardown exclusion, no third-party split, no route-family fingerprint.
  //
  // Delegating routes both paths through one policy. It costs nothing on the
  // duplicate call — `reportApiError` keys an identity WeakSet, so the second
  // pass over an already-reported error is a no-op — and in a build with a DSN
  // it also covers an axios error from an ad-hoc `axios.create()` that never got
  // the interceptor. (Only with a DSN: the gate above returns first without one,
  // so a DSN-less build gets no breadcrumb from this path either. That matches
  // the previous behaviour, which returned at the same gate.)
  //
  // Note this drops `extra`, which `reportApiError` has no channel for. The
  // callers that pass it (`RootErrorBoundary`, `RouteErrorBoundary`) attach
  // chunk-recovery telemetry, and a chunk-load failure is never an AxiosError.
  if (isAxiosError(cause)) {
    dependencies.reportApiError(cause);
    return;
  }

  if (cause instanceof Error) {
    dependencies.captureException(cause, extra ? { extra } : undefined);
    return;
  }

  // A non-Error thrown to a boundary. This used to be the raw string, or
  // `JSON.stringify(error)` for anything else, used directly as the message —
  // which put the thrown value, unsanitized and possibly carrying credentials,
  // into the issue title, and produced the contentless `Error: {}` issue for any
  // object with no enumerable own properties.
  //
  // Thrown strings now take this path too, rather than keeping their own titles.
  // A string is no more sanitized than an object, and a message that varies
  // inside a fixed fingerprint just gives the group an arbitrary title from
  // whichever event happened to land first.
  //
  // So the message is constant and every such throw groups together; the value
  // goes through the same sanitizer the API reporter uses, as evidence rather
  // than identity.
  const safeError = new Error("Non-Error value thrown to a route boundary");
  safeError.name = "NonErrorRouteThrow";
  dependencies.captureException(safeError, {
    extra: { ...extra, thrown_value: getSafeErrorDiagnostics(cause) },
    fingerprint: ["route-boundary-non-error"],
  });
}
